import { AuthLoginResponse } from '@afterpaytouch/portal-api/consumer/auth/types'

const USER_STORAGE_KEY = 'portal_user_hash'
const EXPIRATION_TIME = 60 * 59 * 1000 // backend consumer session persistence is 1 hour, so we need to expire the hash 1 minute before that
// Simple hash function for basic data integrity
const generateHash = (id: number, timestamp: number): string => {
  const data = `${id}-${timestamp}-portal-user`
  let hash = 0
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return hash.toString(36)
}

export const setUserInStorage = (result: AuthLoginResponse): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const timestamp = Date.now()
    const hash = generateHash(result.user.id, timestamp)

    // Store hash and timestamp in localStorage
    const storageData = {
      hash,
      timestamp,
    }
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(storageData))
  } catch (error) {
    console.warn('Failed to store user data in localStorage:', error)
  }
}

export const getUserFromStorage = (): string | null => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const stored = localStorage.getItem(USER_STORAGE_KEY)
    if (!stored) {
      return null
    }

    const storageData = JSON.parse(stored)

    // Validate data structure
    if (!storageData || typeof storageData.hash !== 'string' || typeof storageData.timestamp !== 'number') {
      console.warn('Invalid user data structure in localStorage')
      localStorage.removeItem(USER_STORAGE_KEY)
      return null
    }

    // Check if data is expired (1 hour)
    const now = Date.now()
    if (now - storageData.timestamp > EXPIRATION_TIME) {
      console.info('User data expired, clearing from localStorage')
      localStorage.removeItem(USER_STORAGE_KEY)
      return null
    }

    return storageData.hash
  } catch (error) {
    console.warn('Failed to retrieve user data from localStorage:', error)
    localStorage.removeItem(USER_STORAGE_KEY)
    return null
  }
}

export const clearUserFromStorage = (): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    localStorage.removeItem(USER_STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to clear user data from localStorage:', error)
  }
}
